import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './calendar.scss';
import meetingsData from '../../schedule-items/meetings/meetings.json';
import eventsData from '../../schedule-items/events/events.json';
import EventImageModal from '../event-image-modal/event-image-modal';
import CalendarHeader from '../calendar-header/calendar-header';
import CalendarFilters from '../calendar-filters/calendar-filters';
import CalendarDayCell from '../calendar-day-cell/calendar-day-cell';
import MeetingDetailsModal from '../meeting-details-modal/meeting-details-modal';
import EventDetailsModal from '../event-details-modal/event-details-modal';
import { WEEK_DAYS } from '../../constants/calendar';
import { MOBILE_BREAKPOINT } from '../../constants/global';
import type { CalendarDayItem, CalendarEvent, CalendarFilter, CalendarItem } from './calendar.types';
import { getEventImages } from '../../helpers/eventHelpers';
import { formatWeekRangeParts } from '../../helpers/calendarHelpers';
import { isEventOutsideRef } from '../../utils/domEvents';
import {
  buildCalendarDays,
  buildWeekDays,
  getWeekEnd,
  getWeekStart,
  isSameDay,
  parseLocalDate,
  toDateKey,
} from '../../utils/dateUtils';

export type { CalendarItem } from './calendar.types';

const meetings = meetingsData as CalendarItem[];
const calendarEvents = eventsData as CalendarEvent[];

type CalendarProps = {
  showAll?: boolean;
};

const Calendar = ({ showAll = false }: CalendarProps) => {
  const today = new Date();
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const overflowRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLLabelElement | null>(null);
  const [viewDate, setViewDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const [openPicker, setOpenPicker] = useState<'month' | 'year' | null>(null);
  const [openOverflowDay, setOpenOverflowDay] = useState<string | null>(null);
  const [selectedMeeting, setSelectedMeeting] = useState<CalendarItem | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [selectedEventImageIndex, setSelectedEventImageIndex] = useState(0);
  const [isEventImagePreviewOpen, setIsEventImagePreviewOpen] = useState(false);
  const [calendarFilter, setCalendarFilter] = useState<CalendarFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);
  const [isCompactView, setIsCompactView] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );
  const usesWeeklyView = !showAll || isCompactView;

  const activeMeetings = useMemo(
    () => meetings.filter((meeting) => meeting.active),
    []
  );
  const activeEvents = useMemo(
    () => calendarEvents.filter((event) => event.active && event.origin === 'MSIA'),
    []
  );

  const yearOptions = useMemo(() => {
    const scheduleYears = [
      ...activeMeetings.map((meeting) => parseLocalDate(meeting.date).getFullYear()),
      ...activeEvents.map((event) => parseLocalDate(event.startDate).getFullYear()),
    ];
    const minYear = scheduleYears.length ? Math.min(...scheduleYears) : today.getFullYear() - 2;
    const maxYear = scheduleYears.length ? Math.max(...scheduleYears) : today.getFullYear() + 2;

    const start = Math.min(minYear, today.getFullYear() - 1);
    const end = Math.max(maxYear, today.getFullYear() + 8);

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [activeEvents, activeMeetings, today]);

  const visibleMeetings = usesWeeklyView
    ? activeMeetings.filter((meeting) => {
        const meetingDate = parseLocalDate(meeting.date);
        return meetingDate >= getWeekStart(viewDate) && meetingDate <= getWeekEnd(viewDate);
      })
    : activeMeetings;

  const visibleEvents = usesWeeklyView
    ? activeEvents.filter((event) => {
        const eventStart = parseLocalDate(event.startDate);
        const eventEnd = parseLocalDate(event.endDate || event.startDate);
        return eventStart <= getWeekEnd(viewDate) && eventEnd >= getWeekStart(viewDate);
      })
    : activeEvents;

  useEffect(() => {
    if (usesWeeklyView && selectedEvent && !visibleEvents.includes(selectedEvent)) {
      setSelectedEvent(null);
      setIsEventImagePreviewOpen(false);
    }
  }, [selectedEvent, usesWeeklyView, visibleEvents]);

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const activeFilterCount = (calendarFilter !== 'all' ? 1 : 0) + (normalizedSearchQuery ? 1 : 0);
  const matchesSearch = (item: CalendarItem | CalendarEvent) => {
    if (!normalizedSearchQuery) {
      return true;
    }

    const itemDates = 'startDate' in item ? [item.startDate, item.endDate ?? ''] : [item.date];
    return [item.title, ...itemDates, item.time, item.location, item.description]
      .join(' ')
      .toLowerCase()
      .includes(normalizedSearchQuery);
  };

  const filteredMeetings = calendarFilter === 'events'
    ? []
    : visibleMeetings.filter(matchesSearch);
  const filteredEvents = calendarFilter === 'meetings'
    ? []
    : visibleEvents.filter(matchesSearch);

  const searchOptions = useMemo(() => {
    if (!normalizedSearchQuery) {
      return [];
    }

    const options = [
      ...(calendarFilter === 'events' ? [] : activeMeetings)
        .filter(matchesSearch)
        .map((item) => ({ kind: 'meeting' as const, item })),
      ...(calendarFilter === 'meetings' ? [] : activeEvents)
        .filter(matchesSearch)
        .map((item) => ({ kind: 'event' as const, item })),
    ];

    return options
      .sort((first, second) => {
        if (first.kind !== second.kind) {
          return first.kind === 'event' ? -1 : 1;
        }

        const firstDate = 'startDate' in first.item ? first.item.startDate : first.item.date;
        const secondDate = 'startDate' in second.item ? second.item.startDate : second.item.date;
        return firstDate.localeCompare(secondDate);
      })
      .slice(0, 8);
  }, [activeEvents, activeMeetings, calendarFilter, normalizedSearchQuery]);

  const meetingsByDay = useMemo(() => {
    return filteredMeetings.reduce<Record<string, CalendarItem[]>>((acc, meeting) => {
      const key = meeting.date;
      acc[key] = acc[key] ? [...acc[key], meeting] : [meeting];
      return acc;
    }, {});
  }, [filteredMeetings]);

  const eventsByDay = useMemo(() => {
    return filteredEvents.reduce<Record<string, CalendarEvent[]>>((acc, event) => {
      const startDate = parseLocalDate(event.startDate);
      const endDate = parseLocalDate(event.endDate || event.startDate);

      for (const date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
        const key = toDateKey(date);
        acc[key] = acc[key] ? [...acc[key], event] : [event];
      }

      return acc;
    }, {});
  }, [filteredEvents]);

  const calendarDays = useMemo(
    () => (usesWeeklyView ? buildWeekDays(viewDate) : buildCalendarDays(viewDate)),
    [usesWeeklyView, viewDate]
  );
  const isViewingCurrentMonthYear =
    viewDate.getMonth() === today.getMonth() &&
    viewDate.getFullYear() === today.getFullYear();
  const isViewingCurrentWeek = isSameDay(getWeekStart(viewDate), getWeekStart(today));
  const weekRangeParts = formatWeekRangeParts(viewDate);
  const eventImages = selectedEvent ? getEventImages(selectedEvent) : [];

  const changePeriod = (direction: -1 | 1) => {
    setViewDate((current) =>
      usesWeeklyView
        ? new Date(current.getFullYear(), current.getMonth(), current.getDate() + direction * 7)
        : new Date(current.getFullYear(), current.getMonth() + direction, 1)
    );
    setOpenOverflowDay(null);
  };

  const goToToday = () => {
    setViewDate(
      usesWeeklyView
        ? new Date(today.getFullYear(), today.getMonth(), today.getDate())
        : new Date(today.getFullYear(), today.getMonth(), 1)
    );
    setOpenOverflowDay(null);
  };

  const onMonthChange = (month: number) => {
    setViewDate((current) => new Date(current.getFullYear(), month, 1));
    setOpenOverflowDay(null);
    setOpenPicker(null);
  };

  const onYearChange = (year: number) => {
    setViewDate((current) =>
      usesWeeklyView
        ? new Date(year, current.getMonth(), current.getDate())
        : new Date(year, current.getMonth(), 1)
    );
    setOpenOverflowDay(null);
    setOpenPicker(null);
  };

  const togglePicker = (picker: 'month' | 'year') => {
    setOpenPicker((current) => (current === picker ? null : picker));
  };

  const selectDayItem = (dayItem: CalendarDayItem) => {
    if (dayItem.kind === 'meeting') {
      setSelectedMeeting(dayItem.item);
    } else {
      setSelectedEvent(dayItem.item);
      setSelectedEventImageIndex(0);
    }
    setOpenOverflowDay(null);
  };

  const changeEventImageIndex = (direction: -1 | 1) => {
    setSelectedEventImageIndex((current) => (current + direction + eventImages.length) % eventImages.length);
  };

  const selectSearchOption = (item: CalendarItem | CalendarEvent) => {
    const selectedDate = parseLocalDate('startDate' in item ? item.startDate : item.date);
    setViewDate(
      usesWeeklyView
        ? selectedDate
        : new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
    );
    setOpenOverflowDay(null);
    setSearchQuery('');
    setIsSearchFocused(false);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsCompactView(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    const handleOutsideClick = (event: MouseEvent) => {
      if (isEventOutsideRef(event, pickerRef)) {
        setOpenPicker(null);
      }

      if (isEventOutsideRef(event, overflowRef)) {
        setOpenOverflowDay(null);
      }

      if (isEventOutsideRef(event, searchRef)) {
        setIsSearchFocused(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenPicker(null);
        setOpenOverflowDay(null);
        setSelectedMeeting(null);
        setSelectedEvent(null);
      }

      if (selectedEvent && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
        const images = getEventImages(selectedEvent);
        if (images.length > 1) {
          const direction = event.key === 'ArrowLeft' ? -1 : 1;
          setSelectedEventImageIndex((current) =>
            (current + direction + images.length) % images.length
          );
        }
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [selectedEvent]);

  return (
    <div className="meeting-calendar">
      <CalendarHeader
        usesWeeklyView={usesWeeklyView}
        viewDate={viewDate}
        openPicker={openPicker}
        pickerRef={pickerRef}
        yearOptions={yearOptions}
        weekRangeParts={weekRangeParts}
        isViewingCurrentPeriod={usesWeeklyView ? isViewingCurrentWeek : isViewingCurrentMonthYear}
        onChangePeriod={changePeriod}
        onTogglePicker={togglePicker}
        onMonthChange={onMonthChange}
        onYearChange={onYearChange}
        onGoToToday={goToToday}
      />

      <CalendarFilters
        areFiltersOpen={areFiltersOpen}
        onToggleFiltersOpen={() => setAreFiltersOpen((current) => !current)}
        activeFilterCount={activeFilterCount}
        calendarFilter={calendarFilter}
        onFilterChange={setCalendarFilter}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        isSearchFocused={isSearchFocused}
        onSearchFocus={() => setIsSearchFocused(true)}
        searchRef={searchRef}
        searchOptions={searchOptions}
        onSelectSearchOption={selectSearchOption}
      />

      <div className="meeting-calendar__weekdays">
        {WEEK_DAYS.map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>

      <div className="meeting-calendar__grid">
        {calendarDays.map((day) => {
          const key = toDateKey(day);
          const dayMeetings = meetingsByDay[key] || [];
          const dayEvents = eventsByDay[key] || [];
          const dayItems: CalendarDayItem[] = [
            ...dayMeetings.map((item) => ({ kind: 'meeting' as const, item })),
            ...dayEvents.map((item) => ({ kind: 'event' as const, item })),
          ];

          return (
            <CalendarDayCell
              key={key}
              day={day}
              isInCurrentMonth={day.getMonth() === viewDate.getMonth()}
              isToday={isSameDay(day, today)}
              dayItems={dayItems}
              isOverflowOpen={openOverflowDay === key}
              overflowRef={overflowRef}
              onToggleOverflow={() => setOpenOverflowDay((current) => (current === key ? null : key))}
              onSelectItem={selectDayItem}
            />
          );
        })}
      </div>

      {selectedMeeting && (
        <MeetingDetailsModal meeting={selectedMeeting} onClose={() => setSelectedMeeting(null)} />
      )}

      {selectedEvent && createPortal(
        <>
          {!isEventImagePreviewOpen && (
            <EventDetailsModal
              event={selectedEvent}
              images={eventImages}
              currentImageIndex={selectedEventImageIndex}
              onClose={() => setSelectedEvent(null)}
              onExpandImage={() => setIsEventImagePreviewOpen(true)}
              onChangeImageIndex={changeEventImageIndex}
            />
          )}

          {isEventImagePreviewOpen && (
            <EventImageModal
              selectedImage={{
                images: eventImages,
                alt: selectedEvent.title,
                index: selectedEventImageIndex,
              }}
              onClose={() => setIsEventImagePreviewOpen(false)}
              onChangeIndex={changeEventImageIndex}
            />
          )}
        </>,
        document.body
      )}
    </div>
  );
};

export default Calendar;

